import { Box, Grid, IconButton, Stack, TextField, Typography, useMediaQuery, } from '@mui/material';
import { BatchConfiguration, PreFormBatchConfiguration } from '../types/BatchConfigType';
import { useBatchConfigurationForm } from '../hooks/useBatchConfigurationForm';
import AddIcon from '@mui/icons-material/Add';
import SaveCancelButtons from '../../../core-components/theme/components/SaveCancelComponent/SaveCancelButtons';
import ApiBasedAutoComplete from '../../../globalComponents/apiBasedAutoComplete';
import { renderEquipmentChip } from '../../../components/Triggers/CreateEditTrigger/SubComponents/event/helpers/helper';
import CustomSelect from '../../../core-components/theme/components/CustomSelect';
import { Neutrals, Primary } from '../../../core-components/theme/color-palette/colors';
import Button from '../../../core-components/theme/components/Button/Button';
import { getBatchConfigTypeIconAndColor } from '../helpers/helper';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Theme from '../../../core-components/theme/theme';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SpriteIcon from '../../../core-components/assets/Icon';
import CustomRecurrenceDialog from '../../../core-components/theme/components/Recurrence/RecurrenceEditor';
import BatchFlowDialog from './BatchFlowDialog';

interface BatchConfigurationFormProps {
  initialFormData: BatchConfiguration;
  preFormBatchConfiguration?: PreFormBatchConfiguration;
  assetId: string;
}

export default function BatchConfigurationForm({
  initialFormData,
  preFormBatchConfiguration,
  assetId,
}: BatchConfigurationFormProps) {
  const { t } = useTranslation();
  const { formData, onSave, onCancel, isSavingBatchConfiguration, updateField, errors, handleAddNewStatusCondition, handleConditionChange,
    handleDeleteStatusCondition, getUnselectedStatusOptions, getAvailableActionOptionsForCondition, handleRecurrenceSubmit, isRecurrenceDialogOpen, setIsRecurrenceDialogOpen }
    = useBatchConfigurationForm(initialFormData);
  const isTablet = useMediaQuery(Theme.breakpoints.down('md'));
  const [isBatchFlowDialogOpen, setIsBatchFlowDialogOpen] = useState(false);
  const unselectedStatusOptions = useMemo(
    () => getUnselectedStatusOptions(preFormBatchConfiguration?.batchStatusOptions ?? []),
    [getUnselectedStatusOptions, preFormBatchConfiguration?.batchStatusOptions]
  );
  return (
    <Box component="form" noValidate autoComplete="off">
      <Grid container spacing={5} mt={3}>


        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label={t('batchConfig.batchName', 'Batch Name')}
            value={formData.name || ''}
            onChange={e => updateField('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <ApiBasedAutoComplete
            label={t('batchConfig.batchEquipmentSelection', 'Batch Equipment Selection')}
            apiPath="/batch/getBatchEquipmentAutoComplete"
            searchKey="query"
            keyToPick="equipmentName"
            idKey="equipmentId"
            customActionMethod="POST"
            multiple={true}
            limitTags={1}
            renderChip={renderEquipmentChip}
            selectedOptions={formData?.selectedEquipments ?? []}
            onSelect={opt => {
              updateField('selectedEquipments', opt);
              updateField('batchFlow', null);
            }}
            defaultData={{
              assetId: assetId,
            }}
            error={!!errors.selectedEquipments}
            helperText={errors.selectedEquipments}
            disableCloseOnSelect={true}
            blurOnSelect={false}
            required
          />
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <CustomSelect
            required
            label={t('batchConfig.batchDetectionType', 'Batch Detection Type')}
            value={formData.detectionType || ''}
            onChange={(option) => {
              updateField('detectionType', option?.value || '')
              updateField('selectedEvent', [])
              updateField('startBatchAction', '')
              updateField('timeCycle', null)
            }}
            options={[
              { label: t('batchConfig.none', '— None —'), value: '' },
              ...(preFormBatchConfiguration?.batchDetectionTypes ?? []).map(
                ({ label, value }: any) => ({ label, value })
              ),
            ]}
            error={Boolean(errors.detectionType)}
            errorText={errors.detectionType}
            renderOption={(option) => {
              if (!option.value) return <Typography sx={{ color: "gray" }}>{t('batchConfig.none', '— None —')}</Typography>;
              const { iconClass, color } = getBatchConfigTypeIconAndColor(option.value);
              return (
                <Box display="flex" alignItems="center" sx={{ ms: 2, minHeight: 32, lineHeight: 1 }} gap={1}>
                  <Box color={color} sx={{ display: 'flex', alignItems: 'center' }}>{iconClass}</Box>
                  <Typography sx={{ color: color, fontWeight: "400", lineHeight: 1, display: 'flex', alignItems: 'center' }}>{option.label}</Typography>
                </Box>
              );
            }}
            renderValue={(option) => {
              if (!option?.value) return null;
              const { iconClass, color } = getBatchConfigTypeIconAndColor(option.value);
              return (
                <Box display="flex" alignItems="center" sx={{ ms: 2, minHeight: 32, lineHeight: 1 }} gap={1}>
                  <Box color={color} sx={{ display: 'flex', alignItems: 'center' }}>{iconClass}</Box>
                  <Typography sx={{ color: color, fontWeight: "400", lineHeight: 1, display: 'flex', alignItems: 'center' }}>{option.label}</Typography>
                </Box>
              );
            }}
          />

        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <Button
            disabled= {formData?.detectionType === 'EVENT_COMPLETION'}
            onClick={() => setIsRecurrenceDialogOpen(true)}
            variant="outlined"
            aria-label={t('batchConfig.configureTimeCycleAria', 'Configure Time Cycle')}
            sx={{
              width: '100%', height: '100%', maxHeight: "55px", backgroundColor: "transparent", border: "1px solid rgba(0, 0, 0, 0.23)",
              display: "flex", justifyContent: "center", alignItems: "center", gap: 2
            }}
          >
            <SpriteIcon id='timecycle_batch_icon' width={20} height={20} />
            <Typography sx={{ fontSize: "16px" }}>{t('batchConfig.timeCycleRule', 'Time Cycle Rule')}</Typography>
            <Box sx={{ display: "flex", justifyContent: "right", alignItems: "center", gap: 2 }}>
              <Typography sx={{ fontSize: "13px", color: Neutrals[600] }}>{t('batchConfig.toSet', 'To Set')}</Typography>
              <OpenInNewIcon sx={{ fontSize: "16px", color: Neutrals[600] }} />
            </Box>
          </Button>
        </Grid>
        <Grid item xs={12} sm={2} md={2}>
          <ApiBasedAutoComplete
            label={t('batchConfig.eventSelection', 'Event Selection')}
            apiPath="/batch/getEventsListAutoComplete"
            searchKey="query"
            keyToPick="eventName"
            idKey="eventComponentId"
            customActionMethod="POST"
            selectedOptions={formData?.selectedEvent ?? []}
            onSelect={opt => {
              updateField('selectedEvent', opt);
            }}
            defaultData={{
              assetId: assetId,
            }}
            error={!!errors.selectedEvent}
            helperText={errors.selectedEvent}
            required={formData?.detectionType === 'EVENT_COMPLETION'}
          />
        </Grid>
        <Grid item xs={12} sm={2} md={1}>
              <CustomSelect
                label={t('batchConfig.batchAction', 'Batch Action')}
                value={formData.startBatchAction || ''}
                onChange={option => {
                  updateField('startBatchAction', option?.value);
                }}
                options={[
                  { label: t('batchConfig.none', '— None —'), value: '' },
                  ...(preFormBatchConfiguration?.batchActionOptions ?? []).map(
                    ({ label, value }: any) => ({ label, value })
                  ),
                ]}
                error={Boolean(errors?.startBatchAction)}
                helperText={errors?.startBatchAction}
                required={formData?.detectionType === 'EVENT_COMPLETION'}
              />
            </Grid>


        {/* <Grid container spacing={3} sx={{ mt: 1, mb: 2 }}> */}
        <Grid item xs={12} sm={6} md={3}>
          <ApiBasedAutoComplete
            label={t('batchConfig.trackingSensors', 'Tracking Sensors')}
            apiPath="/batch/getSensorListForAssetAutoComplete"
            searchKey="query"
            keyToPick="sensorName"
            idKey="sensorId"
            customActionMethod="POST"
            multiple={true}
            limitTags={1}
            selectedOptions={formData?.trackingSensors ?? []}
            onSelect={opt => {
              updateField('trackingSensors', opt);
            }}
            defaultData={{
              assetId: assetId
            }}
            error={!!errors.trackingSensors}
            helperText={errors.trackingSensors}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Button
            onClick={() => setIsBatchFlowDialogOpen(true)}
            variant="outlined"
            disabled={!(formData?.selectedEquipments?.length > 0)}
            aria-label={t('batchConfig.configureBatchFlowAria', 'Configure Batch Flow')}
            sx={{
              width: '100%', height: '100%', maxHeight: "55px", backgroundColor: "transparent", border: "1px solid rgba(0, 0, 0, 0.23)",
              display: "flex", justifyContent: "center", alignItems: "center", gap: 2
            }}
          >
            <SpriteIcon id='workflow_icon' width={20} height={20} />
            <Typography sx={{ fontSize: "16px" }}>{t('batchConfig.batchFlow', 'Batch Flow')}</Typography>
            <Box sx={{ display: "flex", justifyContent: "right", alignItems: "center", gap: 2 }}>
              <Typography sx={{ fontSize: "13px", color: Neutrals[600] }}>
                {formData.batchFlow?.nodes?.length > 0 ? t('batchConfig.equipmentCount', '{{count}} Equipment').replace('{{count}}', String(formData.batchFlow.nodes.length)) : t('batchConfig.toSet', 'To Set')}
              </Typography>
              <OpenInNewIcon sx={{ fontSize: "16px", color: Neutrals[600] }} />
            </Box>
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ApiBasedAutoComplete
            label={t('batchConfig.chemicalUsage', 'Chemical Usage')}
            apiPath="/batch/getChemicalsAutoComplete"
            searchKey="query"
            keyToPick="chemicalName"
            idKey="itemId"
            customActionMethod="POST"
            multiple={true}
            limitTags={1}
            selectedOptions={formData?.chemicalUsage ?? []}
            onSelect={opt => {
              updateField('chemicalUsage', opt);
            }}
            defaultData={{
              assetId: assetId,
            }}
          // error={!!errors.chemicalUsage}
          // helperText={errors.chemicalUsage}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Grid container spacing={4}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={t('batchConfig.waterTreatmentValue', 'Water Treatment Value')}
                type="number"
                value={formData.waterTreatment?.value || ''}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (value < 0) return;
                  updateField('waterTreatment', {
                    ...formData.waterTreatment,
                    value,
                  });
                }}
                onKeyDown={(evt) => ["e", "E", "+", "-", "."].includes(evt.key) && evt.preventDefault()}
                required
                error={!!errors?.waterTreatment?.value}
                helperText={errors?.waterTreatment?.value}
              />
            </Grid>
            <Grid item xs={6}>
              <CustomSelect
                label={t('batchConfig.waterTreatmentUnit', 'Water Treatment Unit')}
                value={formData.waterTreatment?.unit || ''}
                onChange={option => {
                  updateField('waterTreatment', {
                    ...formData.waterTreatment,
                    unit: option.value,
                  });
                }}
                options={preFormBatchConfiguration?.waterTreatmentUnits?.map(
                  ({ label, value }) => ({
                    label,
                    value,
                  })
                ) || []}
                error={Boolean(errors?.waterTreatment?.unit)}
                helperText={errors?.waterTreatment?.unit}
                required
              />
            </Grid>
          </Grid>
        </Grid>

        {/* <Grid item xs={12}>
          <Divider
            sx={{
              borderStyle: 'dashed',
              my: 3,
              borderColor: 'rgba(0,0,0,0.12)',
              borderRadius: 1,
              borderImage: `repeating-linear-gradient(90deg, ${Neutrals[300]} 0 16px, transparent 16px 32px) 30`,
            }}
          />
        </Grid> */}

        <Grid item xs={12} sm={4} md={2} >
          <CustomSelect
            label={t('batchConfig.batchType', 'Batch Type')}
            value={formData.batchType || ''}
            onChange={(option) => updateField('batchType', option.value)}
            options={(preFormBatchConfiguration?.batchTypes ?? []).map(
              ({ label, value }) => ({ label, value })
            )}
            error={Boolean(errors.batchType)}
            errorText={errors.batchType}
            required
            renderOption={(option) => {
              const { iconClass, color } = getBatchConfigTypeIconAndColor(option.value);
              return (
                <Box display="flex" alignItems="center" sx={{ ms: 2, minHeight: 32, lineHeight: 1 }} gap={1}>
                  <Box color={color} sx={{ display: 'flex', alignItems: 'center' }}>{iconClass}</Box>
                  <Typography sx={{ color: color, fontWeight: "400", lineHeight: 1, display: 'flex', alignItems: 'center' }}>{option.label}</Typography>
                </Box>
              );
            }}
            renderValue={(option) => {
              if (!option) return null;
              const { iconClass, color } = getBatchConfigTypeIconAndColor(option.value);
              return (
                <Box display="flex" alignItems="center" sx={{ ms: 2, minHeight: 32, lineHeight: 1 }} gap={1}>
                  <Box color={color} sx={{ display: 'flex', alignItems: 'center' }}>{iconClass}</Box>
                  <Typography sx={{ color: color, fontWeight: "400", lineHeight: 1, display: 'flex', alignItems: 'center' }}>{option.label}</Typography>
                </Box>
              );
            }}
          />

        </Grid>

        <Grid item xs={12} sm={6} md={8}>
          <TextField
            fullWidth
            label={t('batchConfig.purpose', 'Purpose')}
            value={formData.purpose || ''}
            onChange={e => updateField('purpose', e.target.value)}
            error={!!errors.purpose}
            helperText={errors.purpose}
            required
          />
        </Grid>

        <Grid item xs={12} sm={2} md={2}>
          <Button
            onClick={handleAddNewStatusCondition}
            variant="contained"
            aria-label={t('batchConfig.batchStatusConditionsAria', 'Batch Status Conditions')}
            disabled={unselectedStatusOptions?.length === 0}
            sx={{ width: '100%', height: '100%', maxHeight: "55px" }}
          >
            <AddIcon sx={{ marginRight: "4px" }} />
            {!isTablet && (
              <Stack >
                {t('batchConfig.batchStatusConditions', 'BATCH STATUS CONDITIONS')}
              </Stack>
            )}
          </Button>
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              height: '50vh',
              border: `1px solid ${Neutrals[100]}`,
              borderRadius: 5,
              p: 5,
              backgroundColor: Neutrals[10],
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                flex: 1,
                width: '100%',
                overflowY: 'auto',
                border: `1px solid ${Primary[100]}`,
                borderRadius: 3,
                p: 4,
                backgroundColor: '#fff',
                boxSizing: 'border-box',
                scrollbarWidth: "none",
                msOverflowStyle: 'none',
                '&::-webkit-scrollbar': { display: 'none' }
              }}
            >
              {formData.attachedConditions?.map((cond: any, index: number) => {
                return (
                  <Grid container spacing={5} alignItems="center" key={index} sx={{
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    p: 1,
                    borderBottom: `1px solid ${Primary[100]}`,
                    minWidth: "500px",

                  }}>
                    <Grid item xs={3} sm={3} md={2} sx={{mb:2}}>
                      <CustomSelect
                        label={t('batchConfig.selectStatus', 'Select Status')}
                        value={cond.status}
                        // variant="standard"
                        disableUnderline
                        required
                        onChange={option => handleConditionChange(index, option.value, 'status')}
                        options={(() => {
                          const allStatusOptions = preFormBatchConfiguration?.batchStatusOptions ?? [];
                          const currentOption = allStatusOptions.find((o: any) => o.value === cond.status);
                          const base = (unselectedStatusOptions ?? []) as Array<{ label: string; value: string }>;
                          const merged = currentOption
                            ? [currentOption, ...base.filter(o => o.value !== currentOption.value)]
                            : base;
                          return merged.map(({ label, value }) => ({ label, value }));
                        })()}
                        // selectSx={{ height: "32px" }}
                        error={Boolean(errors.attachedConditions?.[index]?.status)}
                        errorText={errors.attachedConditions?.[index]?.status}
                        renderOption={(option) => {
                          const { iconClass, color } = getBatchConfigTypeIconAndColor(option.value);
                          return (
                            <Box
                              display="flex"
                              alignItems="center"
                              sx={{ ms: 2, minHeight: 32, lineHeight: 1 }}
                              gap={1}
                            >
                              <Box color={color} sx={{ display: "flex", alignItems: "center" }}>
                                {iconClass}
                              </Box>
                              <Typography
                                sx={{
                                  color: color,
                                  fontWeight: "400",
                                  lineHeight: 1,
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {option.label}
                              </Typography>
                            </Box>
                          );
                        }}
                        renderValue={(option) => {
                          if (!option) return null;
                          const { iconClass, color } = getBatchConfigTypeIconAndColor(option.value);
                          return (
                            <Box
                              display="flex"
                              alignItems="center"
                              sx={{ ms: 2, minHeight: 32, lineHeight: 1 }}
                              gap={1}

                            >
                              <Box color={color} sx={{ display: "flex", alignItems: "center" }}>
                                {iconClass}
                              </Box>
                              <Typography
                                sx={{
                                  color: color,
                                  fontWeight: "400",
                                  lineHeight: 1,
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {option.label}
                              </Typography>
                            </Box>
                          );
                        }}
                      />

                    </Grid>
                    <Grid item xs={4} sm={4} md={4} sx={{mb:2}}>
                      <ApiBasedAutoComplete
                        // variant={'standard'}
                        InputProps={{ disableUnderline: true }}
                        label={t('batchConfig.eventSelection', 'Event Selection')}
                        apiPath="/batch/getEventsListAutoComplete"
                        searchKey="query"
                        keyToPick="eventName"
                        idKey="eventComponentId"
                        customActionMethod="POST"
                        selectedOptions={cond.event}
                        onSelect={opt => {
                          const newConditions = [...(formData.attachedConditions as any[])];
                          newConditions[index] = {
                            ...newConditions[index],
                            event: opt,
                            action: ''
                          };
                          updateField('attachedConditions', newConditions);
                        }}
                        defaultData={{ assetId }}
                        error={!!errors.attachedConditions?.[index]?.condition}
                        helperText={errors.attachedConditions?.[index]?.condition}
                        required
                      />

                    </Grid>
                    <Grid item xs={4} sm={4} md={2}  sx={{mb:2}}>
                    <CustomSelect
                      // variant={"standard"}
                      label={t('batchConfig.conditionAction', 'Condition Action')}
                      value={cond?.action}
                      // selectSx={{ height: "24px" }}
                      onChange={(option: any) => {
                        handleConditionChange(index, option?.value, 'action');
                      }}
                      disableUnderline
                      options={(() => {
                        const allActionOptions = preFormBatchConfiguration?.batchActionOptions || [];
                        return getAvailableActionOptionsForCondition(index, allActionOptions);
                      })()}
                        error={!!errors.attachedConditions?.[index]?.action}
                        helperText={errors.attachedConditions?.[index]?.action}
                        disabled={!cond?.event?.eventComponentId || (() => {
                          const allActionOptions = preFormBatchConfiguration?.batchActionOptions || [];
                          const availableOptions = getAvailableActionOptionsForCondition(index, allActionOptions);
                          return availableOptions.length === 0;
                        })()}
                        required
                      />
                    </Grid>


                    <Grid item xs={1}>
                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <IconButton aria-label={t('common.delete', 'Delete')} onClick={() => handleDeleteStatusCondition(index)}>
                          <DeleteIcon sx={{ fontSize: "24px" }} />
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                )
              })}
            </Box>
          </Box>
        </Grid>



        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <SaveCancelButtons
              onSave={onSave}
              onCancel={onCancel}
              loading={isSavingBatchConfiguration}
              cancelButtonStyle={{
                width: { xs: '50%', sm: '50%', md: '12%' },
                height: "100%",
                maxHeight: "55px"
              }}
              saveButtonStyle={{
                width: { xs: '50%', sm: '50%', md: '12%' },
                height: "100%",
                maxHeight: "55px"
              }}
            />
          </Box>
        </Grid>

      </Grid>
      <CustomRecurrenceDialog
        isStartDateActive={true}
        open={isRecurrenceDialogOpen}
        onClose={() => setIsRecurrenceDialogOpen(false)}
        initialData={formData.timeCycle}
        onSubmit={handleRecurrenceSubmit}
        isCustomRecurrenceAvailable={false}
        isEndDateActive={false}
        isDurationActive={true}
      />
      <BatchFlowDialog
        key={JSON.stringify(formData.selectedEquipments?.map(eq => eq.equipmentId) || [])}
        open={isBatchFlowDialogOpen}
        onClose={() => {
          // updateField('batchFlow', null);
          setIsBatchFlowDialogOpen(false)}}
        selectedEquipments={formData.selectedEquipments || []}
        initialData={formData.batchFlow}
        onSubmit={(data) => {
          updateField('batchFlow', data);
          setIsBatchFlowDialogOpen(false);
        }}
      />
    </Box>
  );
}
